if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.prependMedia = this.dataset.disablePrepend != 'true';
      this.lockFirstMedia = this.dataset.lockFirstMedia === 'true';
      this.hasUserSelectedVariant = false;
      this.hasUserSlidGallery = false;

      if (this.elements.thumbnails) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      } else if (this.lockFirstMedia) {
        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      }
    }

    connectedCallback() {
      if (!this.lockFirstMedia) return;

      this.bindVariantUserIntent();
      this.bindGalleryUserIntent();
      this.pinFirstMedia();

      requestAnimationFrame(() => {
        this.pinFirstMedia();
        requestAnimationFrame(() => this.pinFirstMedia());
      });

      this.observeBadgeClass();
      this.observeGalleryResize();
    }

    disconnectedCallback() {
      this.badgeObserver?.disconnect();
      this.resizeObserver?.disconnect();
    }

    bindVariantUserIntent() {
      const sectionId = this.dataset.section;
      if (!sectionId) return;

      const markUserIntent = () => {
        this.hasUserSelectedVariant = true;
      };

      const roots = [
        document.querySelector(`variant-selects[data-section="${sectionId}"]`),
        document.querySelector(`variant-radios[data-section="${sectionId}"]`),
        document.getElementById(`StickyAtcVariantPicker-${sectionId}`),
        document.getElementById(`quantity-breaks-${sectionId}`)
      ].filter(Boolean);

      roots.forEach((root) => {
        root.addEventListener('pointerdown', markUserIntent, { capture: true });
        root.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowUp' || event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            markUserIntent();
          }
        }, { capture: true });
      });
    }

    bindGalleryUserIntent() {
      const markGalleryIntent = () => {
        this.hasUserSlidGallery = true;
      };

      this.elements.viewer?.addEventListener('pointerdown', markGalleryIntent, { passive: true });
      this.elements.viewer?.addEventListener('touchstart', markGalleryIntent, { passive: true });
      this.elements.viewer?.addEventListener('wheel', markGalleryIntent, { passive: true });
      this.elements.thumbnails?.addEventListener('pointerdown', markGalleryIntent, { passive: true });
    }

    observeBadgeClass() {
      this.badgeObserver = new MutationObserver(() => {
        if (this.hasUserSelectedVariant || this.hasUserSlidGallery) return;
        this.pinFirstMedia();
      });
      this.badgeObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    }

    observeGalleryResize() {
      const slider = this.getSlider();
      if (!slider || typeof ResizeObserver === 'undefined') return;

      this.resizeObserver = new ResizeObserver(() => {
        if (this.hasUserSelectedVariant || this.hasUserSlidGallery) return;
        this.pinFirstMedia();
      });
      this.resizeObserver.observe(slider);

      window.setTimeout(() => {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
      }, 2500);
    }

    getSlider() {
      return this.elements.viewer?.querySelector('[id^="Slider-"]') || this.elements.viewer?.slider;
    }

    getFirstMedia() {
      return this.elements.viewer?.querySelector('[data-media-id]');
    }

    pinFirstMedia() {
      const firstMedia = this.getFirstMedia();
      const slider = this.getSlider();
      if (!firstMedia || !slider) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.toggle('is-active', element === firstMedia);
      });

      slider.scrollTo({ left: 0, behavior: 'auto' });

      if (this.elements.thumbnails) {
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${firstMedia.dataset.mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }
    }

    onSlideChanged(event) {
      if (!event?.detail?.currentElement) return;

      if (this.lockFirstMedia && !this.hasUserSelectedVariant) {
        const firstMedia = this.getFirstMedia();
        const isFirst = firstMedia && event.detail.currentElement === firstMedia;
        if (!isFirst && !this.hasUserSlidGallery) {
          this.pinFirstMedia();
          return;
        }
      }

      this.hasUserSlidGallery = true;
      if (!this.elements.thumbnails) return;
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (
        this.lockFirstMedia &&
        prepend &&
        !this.hasUserSelectedVariant
      ) {
        this.pinFirstMedia();
        return;
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      if (!activeMedia) return;

      this.hasUserSelectedVariant = this.hasUserSelectedVariant || Boolean(prepend);
      this.hasUserSlidGallery = true;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'auto' });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}
